import React, { useMemo } from 'react';
import { Trash } from '@phosphor-icons/react';
import { formatBaseFloorName } from '../HomePage/OverviewPage/SpacesStructure/SpacesStructure';
import { useData } from '../../hooks/useData';
import { TABLES } from '../../constants';
import styles from './SpaceTopBar.module.css';

const KLALI_SPACE_ID = '__klali__';
const CONSTRUCTION_SPACE_ID = '__construction__';

function SpaceTopBar({
  spaces,
  allFloors,
  currentSpaceId,
  onSpaceChange,
  onDeleteSpace,
  spaceData,
  masterSpaces,
}) {
  const isVirtual = currentSpaceId === KLALI_SPACE_ID || currentSpaceId === CONSTRUCTION_SPACE_ID;

  // Lookup map: master_space_id → name (fallback for unnamed spaces)
  const masterSpaceMap = useMemo(() => {
    if (!masterSpaces) return {};
    return Object.fromEntries(masterSpaces.map(ms => [ms.id, ms.name]));
  }, [masterSpaces]);

  // Fetch project items for live cost calculation
  const { data: spaceItems } = useData(TABLES.PROJECT_ITEMS, {
    select: 'id, total_cost, relationships',
    filter: { space_id: currentSpaceId },
    enabled: !!currentSpaceId && !isVirtual,
    realtime: true,
  });

  const totalSpaceCost = useMemo(() => {
    if (!spaceItems) return 0;
    return spaceItems
      .filter(i => i.relationships?.bundle?.role !== 'parent')
      .reduce((sum, i) => sum + (parseFloat(i.total_cost) || 0), 0);
  }, [spaceItems]);

  // Group spaces by floor for optgroup dropdown
  const groupedOptions = useMemo(() => {
    if (!spaces || !allFloors) return [];
    const floorMap = {};
    allFloors.forEach(f => {
      floorMap[f.id] = f;
    });

    // Group spaces by floor_number
    const groups = {};
    spaces.forEach(sp => {
      const floor = sp.project_floors || floorMap[sp.floor_id];
      const floorNum = floor?.floor_number;
      if (floorNum === undefined) return;
      if (!groups[floorNum]) groups[floorNum] = [];
      groups[floorNum].push(sp);
    });

    // Sort floor numbers ascending
    return Object.entries(groups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([floorNum, floorSpaces]) => ({
        label: formatBaseFloorName(Number(floorNum)),
        spaces: floorSpaces,
      }));
  }, [spaces, allFloors]);

  return (
    <div className={styles.topBar}>
      {/* Space selector with optgroups */}
      <div className={styles.spaceSelector}>
        <select
          className={styles.spaceSelect}
          value={currentSpaceId || ''}
          onChange={e => onSpaceChange(e.target.value)}
        >
          <option value={KLALI_SPACE_ID}>כללי - פריטים לכלל הבית</option>
          <option value={CONSTRUCTION_SPACE_ID}>בנייה - עבודות שלד ותשתיות</option>
          {groupedOptions.map(group => (
            <optgroup key={group.label} label={group.label}>
              {group.spaces.map(sp => (
                <option key={sp.id} value={sp.id}>
                  {sp.custom_space_name || masterSpaceMap[sp.master_space_id] || 'חלל ללא שם'}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Stat chips */}
      <div className={styles.statChips}>
        {totalSpaceCost > 0 && (
          <div className={styles.statChip}>
            עלות: <span className={styles.statValue}>₪{totalSpaceCost.toLocaleString()}</span>
          </div>
        )}
        {spaceData?.room_size > 0 && (
          <div className={styles.statChip}>
            שטח: <span className={styles.statValue}>{spaceData.room_size} מ״ר</span>
          </div>
        )}
        {spaceData?.room_height > 0 && (
          <div className={styles.statChip}>
            גובה: <span className={styles.statValue}>{spaceData.room_height} מ׳</span>
          </div>
        )}
      </div>

      {/* Delete button - only for regular spaces */}
      {!isVirtual && onDeleteSpace && (
        <button className={styles.deleteBtn} onClick={onDeleteSpace} title="מחק חלל">
          <Trash size={16} />
        </button>
      )}
    </div>
  );
}

export default React.memo(SpaceTopBar);
